import {AxiosInstance, AxiosResponse} from "axios";
import {
    Contest,
    ContestObjectiveProgress,
    ContestPerformerResult,
    ContestTemplate,
    ContestWithProgress
} from "@/contest/interfaces";
import Product from "@/product/interfaces/Product";
import {PaginatedData} from "@/core/interfaces/api";

interface PerformersQueryParams {
    limit?: number;
}

interface ContestsQueryParams {
    experimental?: boolean;
    status?: null | 'upcoming' | 'ended';
    page?: number;
    limit?: number;
}


export interface SalesConfig {
    group_by: "healthcenter" | "operator";
    laboratory_ids?: number[];
    product_ids?: number[];
    range_ids?: number[];
    prestations?: string[];
    objective?: ContestObjective;
}

export interface ContestObjective {
    target: number;
    type: "collective";
}

export interface ContestCreateRequest {
    name: string;
    description: string;
    start_at: string;
    end_at: string;
    is_published: boolean;
    strategy: string;
    organization_id: number;
    config: SalesConfig;
}

export interface ContestConfigMeta {
    laboratories: {
        id: number;
        name: string;
    }[];
    products: Product[];
    ranges: {
        id: number;
        name: string;
    }[];
    prestations: string[];
}

interface ContestReadResponse {
    data: Contest;
    meta: {
        config: ContestConfigMeta
    }
}


const ContestService = (hdsClient: AxiosInstance) => {
    return {

        /**
         * Get all contests, optionally filtered by status (upcoming or ended) and/or experimental flag.
         */
        async index(params: ContestsQueryParams = {}): Promise<Contest[]> {
            const response = await hdsClient.get('/contests', {params});
            return response.data.data;
        },

        /**
         * Get paginated contests with server-side pagination and sorting.
         */
        async indexWithProgress(params: ContestsQueryParams = {}): Promise<PaginatedData<ContestWithProgress[]>> {
            const response = await hdsClient.get('/contests-progress', {params});
            return response.data;
        },

        /**
         * The contests belonging to the healthcenter's grouping.
         */
        async groupingContests(params: ContestsQueryParams = {}): Promise<Contest[]> {
            const response = await hdsClient.get('/grouping/contests', {params});
            return response.data.data;
        },

        /**
         * Get the top performers of a contest.
         */
        async performers(contestId: number, params: PerformersQueryParams = {}): Promise<{
            data: ContestPerformerResult[],
            current_healthcenter_ranking: ContestPerformerResult | null
            objective_progress: ContestObjectiveProgress | null
        }> {
            const response = await hdsClient.get(`/contests/${contestId}/top-performers`, {params});
            return response.data;
        },

        /**
         * Create a new contest.
         *
         * @param data
         */
        async create(data: unknown): Promise<Contest> {
            return await hdsClient.post('/contests', {data});
        },

        /**
         *  Delete a new contest.
         */
        async delete(contestId: number): Promise<{ deleted: boolean }> {
            return await hdsClient.delete(`/contests/${contestId}`);
        },

        /**
         * Update a contest.
         *
         * @param contestId
         * @param data
         */
        async update(contestId: number, data: unknown): Promise<Contest> {
            return await hdsClient.patch(`/contests/${contestId}`, {data});
        },

        /**
         * Read a contest.
         *
         * @param contestId
         */
        async read(contestId: number): Promise<ContestReadResponse> {
            return await hdsClient.get(`/contests/${contestId}`)
                .then(response => response.data);
        },

        /**
         * Get a list of contest templates.
         */
        async templates(): Promise<ContestTemplate[]> {
            return await hdsClient.get('/contests/templates')
                .then(response => response.data.data);
        },

        /**
         * Export contest data.
         */
        async export(contestId: number): Promise<Blob> {
            return await hdsClient.get(`/contests/${contestId}/export`, {responseType: 'blob'})
                .then((response: AxiosResponse) => response.data)
                .catch(() => new Blob());
        }
    };
};

export default ContestService;
